import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Store as StoreIcon } from 'lucide-react';

const ProductForm = ({ editingProduct, categories, renderCategoryOptions, onSuccess }) => {
  const { token, selectedStore } = useAuth();
  const [stores, setStores] = useState([]);
  const [multiStoreMode, setMultiStoreMode] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [storeCategoryMap, setStoreCategoryMap] = useState({});
  const [allCategories, setAllCategories] = useState({});
  const [formData, setFormData] = useState({
    name: editingProduct ? editingProduct.name : '',
    category_id: editingProduct ? editingProduct.category_id : '',
    min_stock_level: editingProduct ? editingProduct.min_stock_level : 10,
    description: editingProduct ? editingProduct.description || '' : '',
    has_ohms: editingProduct ? editingProduct.has_ohms || false : false,
    has_flavour: editingProduct ? editingProduct.has_flavour || false : false,
    has_colour: editingProduct ? editingProduct.has_colour || false : false,
    has_strength: editingProduct ? editingProduct.has_strength || false : false,
    ohms_options: editingProduct ? editingProduct.ohms_options || [] : [],
    flavour_options: editingProduct ? editingProduct.flavour_options || [] : [],
    colour_options: editingProduct ? editingProduct.colour_options || [] : [],
    strength_options: editingProduct ? editingProduct.strength_options || [] : []
  });

  const [ohmInput, setOhmInput] = useState('');
  const [flavourInput, setFlavourInput] = useState('');
  const [colourInput, setColourInput] = useState('');
  const [strengthInput, setStrengthInput] = useState('');

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    // Fetch categories for each selected store
    selectedStores.forEach(storeId => {
      if (!allCategories[storeId]) {
        fetchCategoriesForStore(storeId);
      }
    });
  }, [selectedStores]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Failed to load stores');
    }
  };

  const fetchCategoriesForStore = async (storeId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories?store_id=${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllCategories(prev => ({ ...prev, [storeId]: data }));
      }
    } catch (error) {
      console.error('Failed to load categories for store');
    }
  };

  const toggleStoreSelection = (storeId) => {
    setSelectedStores(prev => {
      if (prev.includes(storeId)) {
        // Remove from selected
        const newSelected = prev.filter(id => id !== storeId);
        // Also remove from category map
        setStoreCategoryMap(prevMap => {
          const newMap = { ...prevMap };
          delete newMap[storeId];
          return newMap;
        });
        return newSelected;
      } else {
        return [...prev, storeId];
      }
    });
  };

  const selectAllStores = () => {
    if (selectedStores.length === stores.length) {
      setSelectedStores([]);
      setStoreCategoryMap({});
    } else {
      setSelectedStores(stores.map(s => s.id));
    }
  };

  const handleStoreCategoryChange = (storeId, categoryId) => {
    setStoreCategoryMap(prev => ({
      ...prev,
      [storeId]: categoryId
    }));
  };

  const renderStoreCategoryOptions = (storeId) => {
    const storeCategories = allCategories[storeId] || [];
    const mainCategories = storeCategories.filter(c => !c.parent_id);
    
    const options = [];
    for (let i = 0; i < mainCategories.length; i++) {
      const mainCat = mainCategories[i];
      options.push(
        <option key={mainCat.id} value={mainCat.id}>{mainCat.name}</option>
      );
      
      const subcats = storeCategories.filter(c => c.parent_id === mainCat.id);
      for (let j = 0; j < subcats.length; j++) {
        const subcat = subcats[j];
        options.push(
          <option key={subcat.id} value={subcat.id}>  └─ {subcat.name}</option>
        );
      }
    }
    return options;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Multi-store mode
    if (multiStoreMode && !editingProduct) {
      if (selectedStores.length === 0) {
        toast.error('Please select at least one store');
        return;
      }
      
      // Validate that all stores have categories selected
      for (const storeId of selectedStores) {
        if (!storeCategoryMap[storeId]) {
          const store = stores.find(s => s.id === storeId);
          toast.error(`Please select a category for ${store?.name || 'store'}`);
          return;
        }
      }
      
      try {
        const payload = {
          name: formData.name,
          category_ids: storeCategoryMap,
          store_ids: selectedStores,
          min_stock_level: formData.min_stock_level,
          description: formData.description,
          has_ohms: formData.has_ohms,
          has_flavour: formData.has_flavour,
          has_colour: formData.has_colour,
          has_strength: formData.has_strength,
          ohms_options: formData.ohms_options,
          flavour_options: formData.flavour_options,
          colour_options: formData.colour_options,
          strength_options: formData.strength_options
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(data.message);
          onSuccess();
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Operation failed');
        }
      } catch (error) {
        toast.error('Network error');
      }
      return;
    }
    
    // Single store mode
    if (!selectedStore) {
      toast.error('Please select a store first');
      return;
    }
    
    try {
      const url = editingProduct
        ? `${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProduct.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/products`;

      const payload = { ...formData, store_id: selectedStore };

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Product updated' : 'Product created');
        onSuccess();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const addOption = (type) => {
    let input = '';
    let setter = null;
    let field = '';
    
    if (type === 'ohms') {
      input = ohmInput;
      setter = setOhmInput;
      field = 'ohms_options';
    } else if (type === 'flavour') {
      input = flavourInput;
      setter = setFlavourInput;
      field = 'flavour_options';
    } else if (type === 'colour') {
      input = colourInput;
      setter = setColourInput;
      field = 'colour_options';
    } else if (type === 'strength') {
      input = strengthInput;
      setter = setStrengthInput;
      field = 'strength_options';
    }
    
    if (input.trim()) {
      const newOptions = [...formData[field], input.trim()];
      setFormData({ ...formData, [field]: newOptions });
      setter('');
    }
  };

  const renderOptions = (options, type) => {
    const elements = [];
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      elements.push(
        <span key={`${type}-${i}`} className="bg-secondary px-3 py-1 rounded-md text-sm flex items-center gap-2">
          {opt}
          <button type="button" onClick={() => removeOption(type, i)} className="text-destructive">×</button>
        </span>
      );
    }
    return elements;
  };

  const removeOption = (type, index) => {
    const field = `${type}_options`;
    const newOptions = [...formData[field]];
    newOptions.splice(index, 1);
    setFormData({ ...formData, [field]: newOptions });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Multi-store toggle - only show when creating new product */}
      {!editingProduct && stores.length > 1 && (
        <div className="bg-secondary/50 p-3 rounded-md border border-border">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={multiStoreMode}
              onChange={(e) => {
                setMultiStoreMode(e.target.checked);
                if (!e.target.checked) {
                  setSelectedStores([]);
                  setStoreCategoryMap({});
                }
              }}
              className="w-4 h-4"
              data-testid="product-multi-store-toggle"
            />
            <span className="font-mono text-sm font-medium">Add to multiple stores</span>
          </label>
        </div>
      )}

      {/* Multi-store selector with category per store */}
      {multiStoreMode && !editingProduct && (
        <div className="border border-border rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="font-mono text-xs uppercase tracking-widest">Select Stores & Categories</Label>
            <button
              type="button"
              onClick={selectAllStores}
              className="text-xs text-primary hover:underline"
              data-testid="product-select-all-stores-btn"
            >
              {selectedStores.length === stores.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-3">
            {stores.map(store => (
              <div key={store.id} className="p-2 border border-border rounded bg-card">
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={selectedStores.includes(store.id)}
                    onChange={() => toggleStoreSelection(store.id)}
                    className="w-4 h-4"
                    data-testid={`product-store-checkbox-${store.id}`}
                  />
                  <StoreIcon size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium">{store.name}</span>
                </label>
                {selectedStores.includes(store.id) && (
                  <div className="ml-6">
                    <select
                      value={storeCategoryMap[store.id] || ''}
                      onChange={(e) => handleStoreCategoryChange(store.id, e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      data-testid={`product-store-category-${store.id}`}
                    >
                      <option value="">Select category for {store.name}</option>
                      {renderStoreCategoryOptions(store.id)}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
          {selectedStores.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {selectedStores.length} store(s) selected
            </p>
          )}
        </div>
      )}

      <div>
        <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="mt-1.5"
          data-testid="product-name-input"
        />
      </div>

      {/* Single store category selector - only show when not in multi-store mode */}
      {!multiStoreMode && (
        <div>
          <Label className="font-mono text-xs uppercase tracking-widest">Category</Label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
            required={!multiStoreMode}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
            data-testid="product-category-select"
          >
            <option value="">Select category</option>
            {renderCategoryOptions()}
          </select>
        </div>
      )}

      <div>
        <Label className="font-mono text-xs uppercase tracking-widest">Min Stock Level</Label>
        <Input
          type="number"
          value={formData.min_stock_level}
          onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) })}
          required
          className="mt-1.5"
          data-testid="product-min-stock-input"
        />
      </div>

      <div>
        <Label className="font-mono text-xs uppercase tracking-widest">Description</Label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
          data-testid="product-description-input"
        />
      </div>

      <div className="border-t border-border pt-4 mt-4">
        <h3 className="font-heading font-bold text-lg mb-4">Product Variants</h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={formData.has_ohms}
                onChange={(e) => setFormData({ ...formData, has_ohms: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="font-mono text-sm font-medium">Ohms</span>
            </label>
            {formData.has_ohms && (
              <div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={ohmInput}
                    onChange={(e) => setOhmInput(e.target.value)}
                    placeholder="e.g., 0.15, 0.4, 0.8"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => addOption('ohms')} variant="secondary">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {renderOptions(formData.ohms_options, 'ohms')}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={formData.has_flavour}
                onChange={(e) => setFormData({ ...formData, has_flavour: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="font-mono text-sm font-medium">Flavour</span>
            </label>
            {formData.has_flavour && (
              <div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={flavourInput}
                    onChange={(e) => setFlavourInput(e.target.value)}
                    placeholder="e.g., Strawberry, Mint, Tobacco"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => addOption('flavour')} variant="secondary">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {renderOptions(formData.flavour_options, 'flavour')}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={formData.has_colour}
                onChange={(e) => setFormData({ ...formData, has_colour: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="font-mono text-sm font-medium">Colour</span>
            </label>
            {formData.has_colour && (
              <div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={colourInput}
                    onChange={(e) => setColourInput(e.target.value)}
                    placeholder="e.g., Red, Blue, Black"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => addOption('colour')} variant="secondary">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {renderOptions(formData.colour_options, 'colour')}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={formData.has_strength}
                onChange={(e) => setFormData({ ...formData, has_strength: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="font-mono text-sm font-medium">Strength</span>
            </label>
            {formData.has_strength && (
              <div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={strengthInput}
                    onChange={(e) => setStrengthInput(e.target.value)}
                    placeholder="e.g., 0mg, 3mg, 6mg, 12mg"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => addOption('strength')} variant="secondary">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {renderOptions(formData.strength_options, 'strength')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="product-submit-btn">
          {editingProduct ? 'Update' : multiStoreMode ? `Create in ${selectedStores.length} Store(s)` : 'Create'} Product
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;